/**
 * One meditation, as the person practising it sees it.
 *
 * Read or play, and nothing else: no edit, no delete, no copy. The meditation
 * is the therapist's, and what is on this page tonight is what they last saved
 * — the patient is practising with their therapist rather than with a document
 * of their own.
 *
 * The script is shown as it was written, paragraph by paragraph, with the
 * therapist's prompter tags turned into what they mean: `[30s]` is a rest to
 * hold, `[gong]` is a bell. A patient reading `[30s]` mid-meditation would
 * read it aloud in their head, which is the opposite of a silence.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { ArrowLeft, Bell, Clock, Pause as PauseIcon } from "lucide-react";

import PortalMeditationPlayer from "../components/PortalMeditationPlayer";
import { getPortalMeditation } from "../../application/portalServices";
import type { PortalMeditation } from "../../domain/types";
import { splitIntoBlocks, formatDuration } from "../../../meditation";

export default function PortalMeditationPage() {
    const { t } = useTranslation();
    const { meditationId } = useParams<{ meditationId: string }>();
    const [meditation, setMeditation] = useState<PortalMeditation | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!meditationId) return;
        let cancelled = false;

        getPortalMeditation(meditationId)
            .then((data) => {
                if (cancelled) return;
                setMeditation(data);
                setLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setError(true);
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [meditationId]);

    const blocks = splitIntoBlocks(meditation?.script ?? "");
    const runsFor =
        meditation &&
        formatDuration(
            meditation.declared_duration_seconds ??
                meditation.audio_duration_seconds ??
                meditation.estimated_seconds
        );

    return (
        <>
            <Link
                to="/portal/meditations"
                className="mb-5 inline-flex items-center gap-1.5 text-sm font-medium text-content-tertiary hover:text-content-primary"
            >
                <ArrowLeft size={16} aria-hidden="true" />
                {t("portal.meditations.back")}
            </Link>

            {loading && <p className="text-sm text-gray-500">{t("common.loading")}</p>}

            {!loading && error && (
                <p role="alert" className="text-sm text-red-600">
                    {t("portal.meditations.loadError")}
                </p>
            )}

            {!loading && !error && meditation && (
                <article className="glass-effect glass-border rounded-2xl border p-6 shadow-sm sm:p-8">
                    <h1 className="text-2xl font-bold text-content-primary">
                        {meditation.title}
                    </h1>

                    <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-content-tertiary">
                        <span className="flex items-center gap-1.5">
                            <Clock size={14} aria-hidden="true" />
                            {runsFor}
                        </span>
                        {meditation.author_name && (
                            <span>
                                {t("portal.meditations.byAuthor", {
                                    author: meditation.author_name,
                                })}
                            </span>
                        )}
                    </p>

                    {meditation.description && (
                        <p className="mt-5 whitespace-pre-wrap text-sm leading-relaxed text-content-secondary">
                            {meditation.description}
                        </p>
                    )}

                    {meditation.has_audio && (
                        <div className="mt-6">
                            <PortalMeditationPlayer meditation={meditation} />
                        </div>
                    )}

                    {blocks.length > 0 && (
                        <div className="mt-8 space-y-4 border-t border-border-subtle pt-6">
                            {blocks.map((block, index) => {
                                if (block.kind === "text") {
                                    return (
                                        <p
                                            key={index}
                                            className="whitespace-pre-wrap text-base leading-loose text-content-primary"
                                        >
                                            {block.text}
                                        </p>
                                    );
                                }
                                if (block.kind === "gong") {
                                    return (
                                        <p
                                            key={index}
                                            className="flex items-center gap-2 text-sm font-medium text-accent-on-tint"
                                        >
                                            <Bell size={14} aria-hidden="true" />
                                            {t("portal.meditations.bell")}
                                        </p>
                                    );
                                }
                                return (
                                    <p
                                        key={index}
                                        className="flex items-center gap-2 text-sm font-medium text-content-tertiary"
                                    >
                                        <PauseIcon size={14} aria-hidden="true" />
                                        {t("portal.meditations.rest", {
                                            duration: formatDuration(block.seconds),
                                        })}
                                    </p>
                                );
                            })}
                        </div>
                    )}
                </article>
            )}
        </>
    );
}
